import { createAction } from '@reduxjs/toolkit'
import { call, put, select, take, takeLatest } from 'redux-saga/effects'

import { setActiveFloorFromDocChunkId } from '../../../actions/drawable'
import { DocumentMapping } from '../../../models/documentMapping'
import { NormalizedFlag } from '../../../models/flags'
import { Project } from '../../../models/project'
import { getFlagOpeningLink, getFlagOptionPlanContext } from '../../../shared/services/flags/flags-service'
import { selectCurrentEstimatedOptionOverlayChunkIds, setEstimatedOptionOverlayChunkId } from '../../slices/2D'
import {
    selectActiveFloorDocIdIgnoringOverlay,
    selectDocumentMappings,
    selectDrawableActiveFloor,
} from '../../slices/documents'
import { sceneRendered } from '../../slices/loading'
import { selectIsFlagFormOpen, selectMaterialFlags } from '../../slices/materialFlags'
import { selectProject } from '../../slices/project'
import { isInSameLockGroup } from '../../utils/overlayLockGroups'

interface OpenFlagFromPanelPayload {
    flag_id: number
    openingLinkId?: number
    onComplete: () => void
}

export const openFlagFromPanelAction = createAction<OpenFlagFromPanelPayload>('openFlagFromPanel')

export function* openFlagFromPanelSaga(action: ReturnType<typeof openFlagFromPanelAction>) {
    const { flag_id, openingLinkId, onComplete } = action.payload
    const isFlagFormOpen: boolean = yield select(selectIsFlagFormOpen)

    // Preserve the existing pending-switch behavior while a flag is being edited.
    if (isFlagFormOpen) {
        yield call(onComplete)

        return
    }

    const flags: NormalizedFlag[] = yield select(selectMaterialFlags)
    const flag = flags.find((candidate) => candidate.id === flag_id)
    const openingLink = flag ? getFlagOpeningLink(flag, openingLinkId) : null

    if (!flag || !openingLink) {
        yield call(onComplete)

        return
    }

    const project: Project | null = yield select(selectProject)
    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const optionContext = project?.is_master_set
        ? getFlagOptionPlanContext(flag, documentMappings ?? [], [], openingLink.id)
        : null

    if (optionContext?.baseHomeMapping) {
        const baseChunkId = optionContext.baseHomeMapping.document_chunk_id
        const optionChunkId = optionContext.optionMapping.document_chunk_id

        // Match onPlansOptionClick: render the whole locked batch, not just the flagged option.
        const siblingOptionIds = (documentMappings ?? []).reduce<number[]>((ids, mapping) => {
            const isSibling =
                mapping.is_option &&
                mapping.document_chunk_id !== optionChunkId &&
                mapping.additional_data.estimatedBaseHomeId === optionContext.baseHomeMapping!.id &&
                isInSameLockGroup(mapping, optionContext.optionMapping) &&
                (mapping.is_estimation_complete || mapping.overlay_center.length)

            if (isSibling) {
                ids.push(mapping.document_chunk_id)
            }

            return ids
        }, [])

        const activeBaseChunkId: number | null = yield select(selectActiveFloorDocIdIgnoringOverlay)
        const activeOptionChunkIds: number[] = yield select(selectCurrentEstimatedOptionOverlayChunkIds)

        const isOptionContextActive =
            activeBaseChunkId === baseChunkId &&
            [optionChunkId, ...siblingOptionIds].every((chunkId) => activeOptionChunkIds.includes(chunkId))

        if (!isOptionContextActive) {
            yield put(
                setEstimatedOptionOverlayChunkId({
                    opt: optionChunkId,
                    base: baseChunkId,
                    additionalOpts: siblingOptionIds,
                })
            )

            // Wait for every option's sceneRendered, or onComplete may open the flag popup before
            // a sibling's draw closes it again.
            for (let i = 0; i < siblingOptionIds.length + 1; i++) {
                yield take(sceneRendered.type)
            }
        }

        yield call(onComplete)

        return
    }

    const activeFloor = yield select(selectDrawableActiveFloor)

    if (activeFloor?.document_chunk.id !== openingLink.document_chunk_id) {
        yield put(setActiveFloorFromDocChunkId(openingLink.document_chunk_id.toString()))
        yield take(sceneRendered.type)
    }

    yield call(onComplete)
}

export function* watchForOpenFlagFromPanel() {
    yield takeLatest(openFlagFromPanelAction.type, openFlagFromPanelSaga)
}
